/**
 * detectPlatform (session 126s) — sniff the user's runtime context so
 * the "Add to Home Screen" tour step can show the right instructions.
 *
 * Returns one of:
 *   • 'installed' — app is already running as an installed PWA
 *                    (display-mode: standalone OR iOS Safari standalone)
 *   • 'ios-safari' — iPhone/iPad in Safari (the canonical Add-to-Home path)
 *   • 'ios-chrome' — iPhone in Chrome (which CAN'T install directly;
 *                    we tell the user to switch to Safari)
 *   • 'ios-other' — iOS in another browser (Firefox/Edge/etc).
 *                    Same recommendation as ios-chrome.
 *   • 'android'   — Android Chrome / Firefox / Edge / Samsung Internet
 *   • 'desktop'   — anything else (laptop/desktop browser)
 *
 * Pure read on navigator.* + matchMedia; no side effects.
 */
export function detectPlatform() {
  if (typeof window === 'undefined') return 'desktop';
  const ua = window.navigator?.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ reports as Mac in userAgent — also check touch points.
  const isIPad = ua.includes('Macintosh') && (window.navigator?.maxTouchPoints || 0) > 1;
  const isApple = isIOS || isIPad;
  const isAndroid = /Android/i.test(ua);

  // Already installed? Both standalone display-mode + iOS-specific
  // navigator.standalone flag (Apple never adopted the spec property).
  const standalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    || window.navigator?.standalone === true;
  if (standalone) return 'installed';

  if (isApple) {
    // Safari has no Chrome/CriOS/FxiOS/EdgiOS marker; everyone else
    // tunnels through WebKit and adds their own UA marker.
    const isChrome = /CriOS|Chrome/i.test(ua);
    const isFirefox = /FxiOS/i.test(ua);
    const isEdge = /EdgiOS/i.test(ua);
    if (isChrome) return 'ios-chrome';
    if (isFirefox || isEdge) return 'ios-other';
    return 'ios-safari';
  }
  if (isAndroid) return 'android';
  return 'desktop';
}
